import React, { useState, useEffect, useCallback } from 'react';
import Login from './Login';
import Register from './Register';
import ResetPassword from './ResetPassword';
import TaskList from './TaskList';
import Settings from './Settings';
import SetupGuide from './SetupGuide';
import { ToastProvider } from './Toast';
import api, { set401Handler } from './api';
import { mockUserWithoutBarkToken, shouldUseMockEmptyBarkUser } from './mockUser';
import './App.css';

const needsSetupGuide = (userInfo) => !userInfo.barkToken && !userInfo.wechatReceiver;
const shouldOpenSetupGuide = () => new URLSearchParams(window.location.search).get('setupGuide') === '1';

function App() {
  const [currentPage, setCurrentPage] = useState('loading'); // 'loading', 'login', 'register', 'resetPassword', 'setupGuide', 'main'
  const [currentTab, setCurrentTab] = useState('taskList'); // 'taskList', 'settings'

  const enterAppByBarkStatus = useCallback(async () => {
    if (shouldUseMockEmptyBarkUser()) {
      setCurrentPage(shouldOpenSetupGuide() || needsSetupGuide(mockUserWithoutBarkToken) ? 'setupGuide' : 'main');
      return;
    }

    try {
      const response = await api.get('/api/user-info');
      setCurrentPage(shouldOpenSetupGuide() || needsSetupGuide(response.data) ? 'setupGuide' : 'main');
    } catch (error) {
      setCurrentPage('login');
    }
  }, []);

  // 检查是否存在登录态
  useEffect(() => {
    // 检查cookie中是否有token
    const cookies = document.cookie.split(';');
    const tokenCookie = cookies.find(cookie => cookie.trim().startsWith('token='));
    if (tokenCookie || shouldUseMockEmptyBarkUser()) {
      enterAppByBarkStatus();
    } else {
      setCurrentPage('login');
    }
  }, [enterAppByBarkStatus]);

  const handleLogout = useCallback(() => {
    document.cookie = 'token=; path=/; max-age=0';
    setCurrentPage('login');
  }, []);

  // 设置 401 处理函数
  useEffect(() => {
    set401Handler(handleLogout);
  }, [handleLogout]);

  const renderMainContent = () => {
    return (
      <div className="main-container">
        <div className="content-area">
          {currentTab === 'taskList' && (
            <TaskList />
          )}
          {currentTab === 'settings' && (
            <Settings onLogout={handleLogout} />
          )}
        </div>
        <div className="bottom-tab">
          <button
            className={`tab-button ${currentTab === 'taskList' ? 'active' : ''}`}
            onClick={() => setCurrentTab('taskList')}
          >
            任务
          </button>
          <button
            className={`tab-button ${currentTab === 'settings' ? 'active' : ''}`}
            onClick={() => setCurrentTab('settings')}
          >
            设置
          </button>
        </div>
      </div>
    );
  };

  return (
    <ToastProvider>
      <div className="App">
        {currentPage === 'login' && (
          <Login
            onSwitchToRegister={() => setCurrentPage('register')}
            onSwitchToResetPassword={() => setCurrentPage('resetPassword')}
            onLoginSuccess={enterAppByBarkStatus}
          />
        )}
        {currentPage === 'register' && (
          <Register
            onSwitchToLogin={() => setCurrentPage('login')}
            onRegisterSuccess={() => setCurrentPage('main')}
          />
        )}
        {currentPage === 'resetPassword' && (
          <ResetPassword onSwitchToLogin={() => setCurrentPage('login')} />
        )}
        {currentPage === 'loading' && (
          <div className="app-loading">正在检查登录状态...</div>
        )}
        {currentPage === 'setupGuide' && (
          <SetupGuide
            onComplete={() => setCurrentPage('main')}
            onLogout={handleLogout}
          />
        )}
        {currentPage === 'main' && renderMainContent()}
      </div>
    </ToastProvider>
  );
}

export default App;
